import { useMemo } from "react";

/**
 * Ambient dust-mote particle field — Palantir-grade atmospheric depth.
 * CSS-only (no JS animation), fully accessible (aria-hidden, pointer-events: none),
 * deterministic per-mount so it doesn't flicker on re-renders.
 *
 *  - `count`     — number of motes (default 28). Keep ≤ 40 for perf.
 *  - `tone`      — "sapphire" (default), "platinum", or "mixed".
 *  - `density`   — opacity multiplier 0.4 – 1.0 (default 0.7).
 */
export default function AmbientParticles({ count = 28, tone = "sapphire", density = 0.7 }) {
  const motes = useMemo(() => {
    // Deterministic pseudo-random using a seed so dev hot-reload + remount are stable
    const seed = count * 1009 + (tone === "platinum" ? 17 : tone === "mixed" ? 31 : 7);
    let s = seed;
    const rand = () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
    const items = [];
    for (let i = 0; i < count; i++) {
      const left = rand() * 100;
      const top  = rand() * 100;
      const size = 1 + rand() * 2.4;            // 1.0 – 3.4 px
      const dur  = 14 + rand() * 22;            // 14 – 36 s drift
      const delay = -rand() * dur;              // negative delay so motion is staggered
      const driftX = (rand() * 90 - 45).toFixed(1);  // ± 45 px
      const driftY = (rand() * 70 - 35).toFixed(1);  // ± 35 px
      const twinkDur = 3 + rand() * 4;
      const twinkDelay = -rand() * twinkDur;
      const isGold = tone === "mixed" && rand() > 0.78;
      const color = isGold
        ? "rgba(229, 199, 127, %A)"
        : tone === "platinum"
          ? "rgba(229, 231, 235, %A)"
          : "rgba(147, 197, 253, %A)";
      const baseAlpha = (0.35 + rand() * 0.55) * density;
      items.push({
        left, top, size, dur, delay, driftX, driftY, twinkDur, twinkDelay,
        fill: color.replace("%A", baseAlpha.toFixed(2)),
        glow: color.replace("%A", Math.min(1, baseAlpha + 0.4).toFixed(2)),
      });
    }
    return items;
  }, [count, tone, density]);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true" data-testid="ambient-particles">
      {motes.map((m, i) => (
        <span
          key={i}
          className="ambient-mote"
          style={{
            position: "absolute",
            left: `${m.left}%`,
            top:  `${m.top}%`,
            width: `${m.size}px`,
            height: `${m.size}px`,
            borderRadius: "50%",
            background: m.fill,
            boxShadow: `0 0 ${(m.size * 3).toFixed(0)}px ${m.glow}`,
            // Two concurrent CSS animations: long drift + faster twinkle (defined in index.css)
            animation: `moteDrift ${m.dur}s linear ${m.delay}s infinite, moteTwinkle ${m.twinkDur}s ease-in-out ${m.twinkDelay}s infinite`,
            // CSS vars consumed by moteDrift keyframes
            "--mote-dx": `${m.driftX}px`,
            "--mote-dy": `${m.driftY}px`,
            willChange: "transform, opacity",
          }}
        />
      ))}
    </div>
  );
}
